<script setup>
import { computed, ref } from 'vue'
import FormInput from '../Atoms/FormInput.vue'
import FormButton from '../Atoms/FormButton.vue'
import FormRadio from '../Atoms/FormRadio.vue'
import IconBase from '../icon/IconBase.vue'
import IconSearch from '../icon/IconSearch.vue'
import IconCheckmark from '../icon/IconCheckmark.vue'

const props = defineProps({
	items: { type: Array, default: () => [] },
	modelValue: { type: Array, default: () => [] },
	// 'all' | 'specific'
	mode: { type: String, default: 'all' },
	disabled: { type: Boolean, default: false },
	// Entitlement lock: Specific is unavailable on lower plans.
	scopeLocked: { type: Boolean, default: false },
	lockMessage: { type: String, default: '' },
	// 'streams' | 'folders' — noun used in the copy.
	noun: { type: String, default: 'streams' },
	// Wording for the two choices, when "All/Specific <noun>" does not say it
	// well enough. Workspaces are access rather than content, so they read
	// "All workspaces / Selected workspaces".
	allLabel: { type: String, default: '' },
	allHint: { type: String, default: '' },
	specificLabel: { type: String, default: '' },
	specificHint: { type: String, default: '' },
	// Items that cannot be picked, with the reason shown beside them.
	lockedIds: { type: Array, default: () => [] },
	lockedNote: { type: String, default: '' },
	// unique radio group name
	name: { type: String, required: true },
})

const emit = defineEmits(['update:modelValue', 'update:mode'])

const search = ref('')

const selected = computed(() => (Array.isArray(props.modelValue) ? props.modelValue : []))
const selectedCount = computed(() => selected.value.length)
// Locked items are not on offer, so they are not part of "3 of 5 selected"
// either, and "Select all" has nothing to say about them.
const itemCount = computed(() => props.items.filter((item) => !props.lockedIds.includes(item._id)).length)
const allSelected = computed(() => itemCount.value > 0 && selectedCount.value >= itemCount.value)
const isSpecific = computed(() => props.mode === 'specific')
const invalid = computed(() => isSpecific.value && selectedCount.value === 0)

const filteredItems = computed(() => {
	const query = search.value.trim().toLowerCase()
	if (!query) return props.items
	return props.items.filter((item) => String(item.name || '').toLowerCase().includes(query))
})

const isSelected = (id) => selected.value.includes(id)
const isLocked = (id) => props.lockedIds.includes(id)

const setMode = (mode) => {
	if (props.disabled) return
	if (mode === 'specific' && props.scopeLocked) return
	emit('update:mode', mode)
}

const setIds = (ids) => {
	if (props.disabled) return
	emit('update:modelValue', ids)
}

const toggle = (id) => {
	if (props.disabled || isLocked(id)) return
	if (isSelected(id)) {
		setIds(selected.value.filter((itemId) => itemId !== id))
		return
	}
	setIds([...selected.value, id])
}

const selectAll = () => {
	if (props.disabled) return
	setIds(allSelected.value ? [] : props.items.filter((item) => !isLocked(item._id)).map((item) => item._id))
}
</script>

<template>
	<div class="mt-2 flex flex-col gap-3 rounded-lg bg-surface-1 p-3">
		<div class="flex flex-col gap-2">
			<label
				class="flex items-start gap-2 cursor-pointer"
				:class="disabled ? 'opacity-60 cursor-not-allowed' : ''"
			>
				<FormRadio
					:name="name"
					input-value="all"
					:model-value="mode"
					:disabled="disabled"
					@update:model-value="setMode"
				/>
				<span class="min-w-0">
					<span class="text-sm text-surface-text-2">{{ allLabel || `All ${noun}` }}</span>
					<span class="block text-tiny text-surface-7">
						{{ allHint || `New ${noun} are included automatically.` }}
					</span>
				</span>
			</label>
			<label
				class="flex items-start gap-2"
				:class="(disabled || scopeLocked) ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'"
			>
				<FormRadio
					:name="name"
					input-value="specific"
					:model-value="mode"
					:disabled="disabled || scopeLocked"
					@update:model-value="setMode"
				/>
				<span class="min-w-0">
					<span class="text-sm text-surface-text-2">{{ specificLabel || `Specific ${noun}` }}</span>
					<span v-if="specificHint" class="block text-tiny text-surface-7">{{ specificHint }}</span>
					<span v-else class="block text-tiny text-surface-7">
						Pick at least one. New {{ noun }} are <strong class="font-medium">not</strong> included.
					</span>
				</span>
			</label>
		</div>

		<p v-if="scopeLocked && lockMessage" class="mb-0 text-tiny text-surface-7">
			{{ lockMessage }}
			<a href="/app/subscribe" target="_blank" rel="noopener noreferrer" class="!text-white underline hover:!text-white">Upgrade now</a>
		</p>

		<template v-if="isSpecific && !scopeLocked">
			<div class="flex flex-wrap items-center gap-2">
				<p class="mb-0 mr-auto text-tiny" :class="invalid ? 'text-error' : 'text-surface-7'">
					{{ selectedCount ? `${selectedCount} of ${itemCount} selected` : 'Select at least one' }}
				</p>
				<div class="relative w-full sm:w-44">
					<IconBase class="text-surface-8 absolute left-3 top-1/2 -translate-y-1/2 shrink-0 z-10 pointer-events-none">
						<IconSearch class="w-3.5 h-3.5" />
					</IconBase>
					<FormInput v-model="search" size="sm" customClasses="!pl-9" placeholder="Search" :disabled="disabled" />
				</div>
				<FormButton type="link" size="sm" :disabled="disabled || !items.length" @click="selectAll">
					{{ allSelected ? 'Clear' : 'Select all' }}
				</FormButton>
			</div>
			<ul class="flex flex-col gap-px max-h-56 overflow-y-auto -mx-1 px-1 py-0.5">
				<li v-for="item in filteredItems" :key="item._id">
					<button
						type="button"
						role="checkbox"
						:aria-checked="isSelected(item._id)"
						class="flex items-center gap-2.5 w-full h-9 px-2 rounded-md text-left transition-colors"
						:class="[
							isSelected(item._id) ? 'bg-surface-3' : 'hover:bg-surface-2',
							isLocked(item._id) ? 'opacity-60 cursor-not-allowed hover:bg-transparent' : '',
						]"
						:disabled="disabled || isLocked(item._id)"
						@click="toggle(item._id)"
					>
						<span
							class="flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors"
							:class="isSelected(item._id) ? 'bg-main border-main text-white' : 'border-surface-5 text-transparent'"
						>
							<IconBase class="w-3 h-3"><IconCheckmark /></IconBase>
						</span>
						<span
							class="min-w-0 truncate text-sm"
							:class="isSelected(item._id) ? 'text-white' : 'text-surface-9'"
						>{{ item.name }}</span>
						<span v-if="isLocked(item._id) && lockedNote" class="ml-auto shrink-0 text-tiny text-surface-6">
							{{ lockedNote }}
						</span>
					</button>
				</li>
				<li v-if="!filteredItems.length">
					<p class="text-tiny text-surface-7 px-2 py-2 mb-0">No {{ noun }} match your search</p>
				</li>
			</ul>
		</template>
	</div>
</template>
